/**
 * L2 — one job in full: its run history, its duration trend, and the
 * acknowledgement log.
 *
 * The acknowledgement form is the part that must not be lost in automation
 * (concept §5). The spreadsheet's `Failure Comments` and `Solution` columns
 * hold institutional knowledge — "Job disabled and server moved", "Restore
 * points changed from…" — and that judgement is the valuable half of the daily
 * check. So the board is not read-only: whoever is on duty acknowledges a
 * failure with a comment and a resolution, stored against the run, attributed
 * and timestamped by the server from the signed-in session. That turns the rota
 * from initials typed into a column header into a fact recorded automatically,
 * and it is what makes the audit answer "every failure in the last 12 months,
 * who acknowledged it, and what was done" rather than "we keep a spreadsheet".
 *
 * The form is gated by the ordinary session, not by admin — the rota is whoever
 * is on duty, and `require_admin` is a dev-only bypass in production.
 *
 * GAP: the concept asks for the backup-copy / GFS chain here. The ingest
 * contract carries jobs, not the chain between them, so there is no field to
 * render — Veeam copy jobs arrive as ordinary jobs with a `BackupCopy` type.
 * Rather than inferring a chain from name prefixes and presenting a guess as a
 * fact, this drawer omits it until the collector reports the relationship.
 */

import { Chart } from "@norican/dataviz";
import { Icon, MessageSquarePlus } from "@norican/icons";
import {
  Alert,
  Button,
  DataGrid,
  EmptyState,
  Field,
  Input,
  Skeleton,
  Textarea,
  Timeline,
  Typography,
} from "@norican/react";
import { type FormEvent, useState } from "react";

import { DetailDrawer } from "@/components/board";
import {
  type BackupAnnotationRow,
  type BackupRunRow,
  useBackupJobRuns,
  useCreateBackupAnnotation,
} from "@/lib/api";
import { formatCount, formatRelativeTime } from "@/lib/charts/config";

import { StatusBadge } from "./job-columns";
import { BACKUP_JOBS_PATH } from "./params";
import {
  formatDuration,
  formatTimestamp,
  scheduleCaption,
  sourceLabel,
  statusStyle,
} from "./status";

/** How many runs the duration trend and the history grid show. */
const RUN_LIMIT = 30;

interface JobDrawerProps {
  name: string | null;
  source: string | null;
  onClose: () => void;
}

export function JobDrawer({ name, source, onClose }: JobDrawerProps) {
  const detail = useBackupJobRuns(name, source);
  const open = name != null;
  const data = detail.data;
  const job = data?.job ?? null;

  const runs = (data?.runs ?? []).slice(0, RUN_LIMIT);
  // Oldest on the left, so the trend reads left to right like every other time
  // axis on the estate.
  const trend = [...runs].reverse().map((run) => ({
    started_at: run.started_at,
    label: formatTimestamp(run.started_at),
    minutes: run.duration_sec == null ? null : Math.round((run.duration_sec / 60) * 10) / 10,
  }));

  const style = job ? statusStyle(job.status) : null;

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={name ?? "Job"}
      breadcrumbs={[
        { label: "Backup", to: "/backup" },
        { label: "Jobs", to: BACKUP_JOBS_PATH },
      ]}
      items={
        job
          ? [
              { label: "Source", value: sourceLabel(job.source) },
              { label: "Result", value: <StatusBadge status={job.status} size="sm" /> },
              { label: "What that means", value: style?.meaning ?? "—" },
              { label: "Site", value: job.site ?? "Not encoded in the name" },
              { label: "SLA tier", value: job.sla ?? "Untiered" },
              { label: "Type", value: job.job_type ?? "—" },
              { label: "Last run", value: formatTimestamp(job.last_start) },
              { label: "Duration", value: formatDuration(job.duration_sec) },
              {
                label: "Last success",
                value: job.last_success
                  ? `${formatRelativeTime(job.last_success)} · ${formatDuration(
                      job.restore_point_age_sec,
                    )} old`
                  : "Never — there is no restore point",
              },
              {
                // The caption a bare "missed" badge cannot give: how often the
                // job was supposed to run, and how long ago it actually did.
                label: "Schedule",
                value: scheduleCaption(job),
              },
              {
                // Restore evidence — the only thing that proves a backup can be
                // recovered from, and what an audit asks for. Unread and never
                // are different answers and read as different answers.
                label: "Last successful restore",
                value: !data?.restore_evidence_readable
                  ? "Unknown — the restore history could not be read"
                  : data.last_restore
                    ? `${formatRelativeTime(data.last_restore)} · ${formatTimestamp(
                        data.last_restore,
                      )}`
                    : "Never — no restore has proven this backup",
              },
              { label: "Next run", value: formatTimestamp(job.next_run) },
              { label: "Runs in window", value: formatCount(job.runs_in_window) },
              ...(job.source === "azure-backup"
                ? [
                    { label: "Health", value: job.health_status ?? "Unknown" },
                    { label: "Protection state", value: job.protection_state ?? "Unknown" },
                    { label: "Protection status", value: job.protection_status ?? "Unknown" },
                    { label: "Policy", value: job.policy_name ?? "Unknown" },
                  ]
                : []),
            ]
          : undefined
      }
    >
      {detail.error && (
        <Alert tone="danger" title="Couldn't load this job">
          {detail.error instanceof Error ? detail.error.message : "The NOVA API did not respond."}
        </Alert>
      )}

      {detail.isPending && !data ? (
        <Skeleton height="18rem" />
      ) : (
        data && (
          <div className="flex flex-col gap-6">
            {!data.configured && (
              <Typography variant="body-sm" tone="muted">
                {data.status_detail}
              </Typography>
            )}
            {job == null && data.configured && (
              <Alert tone="warning" title="No current state for this job">
                The job is only known from its run history — the latest report did not carry it, so
                it may have been renamed or removed.
              </Alert>
            )}
            {/* The blind spot named. `config_known: false` means the source
                could not read this job's schedule, and `missed_known: false`
                means it therefore declined to decide whether the job is late.
                That undecided answer is stored as `false` to fill a NOT NULL
                column, which reads exactly like "ran fine" — so it has to be
                said out loud in the one place someone is looking at this job. */}
            {job != null && (job.config_known === false || !job.missed_known) && (
              <Alert tone="warning" title="This job's schedule is unknown">
                {job.config_known === false
                  ? "The Veeam API returned an error for the page carrying this job's configuration, so the collector stepped over it. Its sessions below are unaffected — only the schedule is missing."
                  : "The source did not report a schedule wide enough to judge against its lookback window."}{" "}
                {!job.missed_known
                  ? '"Did not run" cannot be decided without one, so this job is not counted as missed — and it is not proven fine either.'
                  : ""}
              </Alert>
            )}
            <RunTrend trend={trend} />
            <RunHistory
              runs={runs}
              total={data.total}
              truncated={data.truncated}
              days={data.days}
            />
            <AnnotationLog
              annotations={data.annotations}
              name={name}
              source={source ?? job?.source ?? null}
              latestRunStart={data.runs[0]?.started_at ?? null}
            />
          </div>
        )
      )}
    </DetailDrawer>
  );
}

interface TrendPoint {
  started_at: string;
  label: string;
  minutes: number | null;
}

/**
 * Duration trend. A run with no usable duration renders as a **gap**, not as
 * zero — `connectNulls={false}` leaves the null out of the line, which is
 * exactly the "no data ≠ zero" behaviour the rules require (§5).
 */
function RunTrend({ trend }: { trend: TrendPoint[] }) {
  const measured = trend.filter((point) => point.minutes != null);
  if (measured.length < 2) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="body-sm" tone="muted">
        Run duration in minutes, oldest first — a gap is a run whose duration the source could not
        report
      </Typography>
      <Chart.Container
        config={{ minutes: { label: "Duration (min)", color: "var(--norican-chart-1)" } }}
        className="h-48"
        aria-label="Run duration trend for this job, in minutes"
      >
        <Chart.LineChart data={trend} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
          <Chart.XAxis {...Chart.axisProps} dataKey="label" hide />
          <Chart.YAxis {...Chart.axisProps} width={40} />
          <Chart.Tooltip content={<Chart.TooltipContent />} />
          <Chart.Line
            {...Chart.seriesDefaults}
            dataKey="minutes"
            type="monotone"
            stroke="var(--color-minutes)"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
        </Chart.LineChart>
      </Chart.Container>
    </div>
  );
}

function RunHistory({
  runs,
  total,
  truncated,
  days,
}: {
  runs: BackupRunRow[];
  total: number;
  truncated: boolean;
  days: number;
}) {
  // Azure reports restores and policy changes through the same job feed as
  // backups, so a run's operation is not decoration here — without it a restore
  // reads as a backup that ran. The column only appears when the history
  // actually holds something other than a backup, so a pure Veeam job (whose
  // every session is a backup by contract) is not given a column of one word.
  const mixed = runs.some((run) => run.operation_kind !== "backup");
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="body-sm" tone="muted">
        Run history over {days} days · newest first
        {total > runs.length
          ? ` (showing ${formatCount(runs.length)} of ${formatCount(total)})`
          : ""}
        {truncated
          ? " — the response itself was capped, so the oldest runs are not in this list"
          : ""}
        {mixed
          ? " — not every session here is a backup, so the counts on the board exclude the ones that are not"
          : ""}
      </Typography>
      {runs.length === 0 ? (
        <EmptyState
          size="sm"
          title="No runs recorded in this window"
          description="Run history accrues from the first collection onward and is kept for 13 months."
        />
      ) : (
        <DataGrid<BackupRunRow>
          data={runs}
          getRowId={(run) => `${run.source}:${run.started_at}`}
          density="compact"
          aria-label="Every recorded run of this job"
          columns={[
            {
              id: "status",
              header: "Result",
              accessor: (run) => run.status,
              cell: (run) => <StatusBadge status={run.status} />,
            },
            {
              id: "started",
              header: "Started",
              accessor: (run) => run.started_at,
              cell: (run) => (
                <span className="text-fg-muted text-xs tabular-nums">
                  {formatTimestamp(run.started_at)}
                </span>
              ),
            },
            ...(mixed
              ? [
                  {
                    id: "operation",
                    header: "Operation",
                    accessor: (run: BackupRunRow) => run.operation ?? "",
                    cell: (run: BackupRunRow) => (
                      <span className="text-fg-muted text-xs">
                        {run.operation ?? "Not recorded"}
                        {run.operation_kind !== "backup" && run.operation_kind !== "unknown"
                          ? " · not a backup"
                          : ""}
                      </span>
                    ),
                  },
                ]
              : []),
            {
              id: "duration",
              header: "Duration",
              accessor: (run) => run.duration_sec ?? -1,
              align: "end",
              cell: (run) => (
                <span className="text-fg-muted text-xs tabular-nums">
                  {formatDuration(run.duration_sec)}
                </span>
              ),
            },
            {
              id: "acks",
              header: "Acknowledged",
              accessor: (run) => run.annotations.length,
              align: "end",
              cell: (run) => (
                <span className="text-fg-muted text-xs">
                  {run.annotations.length > 0 ? formatCount(run.annotations.length) : "—"}
                </span>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

interface AnnotationLogProps {
  annotations: BackupAnnotationRow[];
  name: string | null;
  source: string | null;
  latestRunStart: string | null;
}

/** Prior annotations plus the form that adds one. */
function AnnotationLog({ annotations, name, source, latestRunStart }: AnnotationLogProps) {
  const create = useCreateBackupAnnotation(name, source);
  const [comment, setComment] = useState("");
  const [resolution, setResolution] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = comment.trim();
    if (trimmed.length === 0) {
      return;
    }
    create.mutate(
      {
        comment: trimmed,
        resolution: resolution.trim() === "" ? null : resolution.trim(),
        run_started_at: latestRunStart,
      },
      {
        onSuccess: () => {
          setComment("");
          setResolution("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <Typography variant="body-sm" tone="muted">
        Acknowledgements · {formatCount(annotations.length)} recorded
      </Typography>

      {annotations.length === 0 ? (
        <EmptyState
          size="sm"
          title="Nobody has acknowledged this job yet"
          description="Comments and resolutions recorded here replace the spreadsheet's Failure Comments and Solution columns."
        />
      ) : (
        <Timeline
          orientation="vertical"
          items={annotations.map((annotation) => ({
            id: annotation.id,
            tone: annotation.resolution ? ("success" as const) : ("accent" as const),
            timestamp: formatRelativeTime(annotation.acknowledged_at),
            title: <span className="text-fg font-medium">{annotation.author_upn}</span>,
            description: (
              <span className="text-fg-muted flex flex-col gap-1 text-xs">
                <span>{annotation.comment}</span>
                {annotation.resolution && (
                  <span className="text-fg">Resolution: {annotation.resolution}</span>
                )}
                <span>
                  {annotation.run_started_at
                    ? `Against the run started ${formatTimestamp(annotation.run_started_at)}`
                    : "A note about the job, not a specific run"}
                </span>
              </span>
            ),
          }))}
        />
      )}

      <form onSubmit={submit} className="border-border flex flex-col gap-3 border-t pt-4">
        <Field
          label="What happened"
          hint="The judgement that used to go in Failure Comments — why it failed, or why it is expected."
          required
        >
          <Textarea
            name="comment"
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Job disabled and server moved to the new host…"
          />
        </Field>
        <Field
          label="Resolution"
          hint="What was done. Leave it empty if the failure is still open."
        >
          <Input
            name="resolution"
            value={resolution}
            onChange={(event) => setResolution(event.target.value)}
            placeholder="Restore points changed from 7 to 14"
          />
        </Field>

        {create.error != null && (
          <Alert tone="danger" title="The acknowledgement was not saved">
            {create.error instanceof Error
              ? create.error.message
              : "The NOVA API rejected the write. Nothing was recorded."}
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            startIcon={<Icon icon={MessageSquarePlus} size="sm" />}
            loading={create.isPending}
            disabled={comment.trim().length === 0}
          >
            Acknowledge
          </Button>
        </div>
      </form>
    </div>
  );
}
